/**
 * Login Lockout Utility
 * Implements progressive account lockout after failed login attempts
 *
 * Security features:
 * - Tracks failed login attempts
 * - Progressive lockout delays (1 min, 5 min, 15 min, 30 min)
 * - Stores in localStorage with expiry
 * - Clears on successful login
 * - Prevents brute force attacks
 */

const LOCKOUT_KEY = 'login_lockout_data'

// Progressive lockout thresholds (attempts → lockout duration in minutes)
const LOCKOUT_THRESHOLDS = [
  { attempts: 3, duration: 1 },    // 3 failed attempts = 1 minute lockout
  { attempts: 5, duration: 5 },    // 5 failed attempts = 5 minute lockout
  { attempts: 7, duration: 15 },   // 7 failed attempts = 15 minute lockout
  { attempts: 10, duration: 30 }   // 10+ failed attempts = 30 minute lockout
]

/**
 * Get lockout duration in minutes based on attempt count
 */
function getLockoutDuration(attempts) {
  // Find the applicable threshold (highest one that matches)
  for (let i = LOCKOUT_THRESHOLDS.length - 1; i >= 0; i--) {
    if (attempts >= LOCKOUT_THRESHOLDS[i].attempts) {
      return LOCKOUT_THRESHOLDS[i].duration
    }
  }
  return 0 // No lockout if below minimum threshold
}

/**
 * Get current lockout data
 */
function getLockoutData() {
  try {
    const data = localStorage.getItem(LOCKOUT_KEY)
    if (!data) return null

    const parsed = JSON.parse(data)

    // Check if lockout has expired
    if (parsed.lockedUntil && Date.now() > parsed.lockedUntil) {
      // Lockout expired, clear it
      localStorage.removeItem(LOCKOUT_KEY)
      return null
    }

    return parsed
  } catch (err) {
    console.error('Error reading lockout data:', err)
    return null
  }
}

/**
 * Save lockout data
 */
function saveLockoutData(data) {
  try {
    localStorage.setItem(LOCKOUT_KEY, JSON.stringify(data))
  } catch (err) {
    console.error('Error saving lockout data:', err)
  }
}

/**
 * Check if account is currently locked out
 * @returns {object|null} { isLocked: true, remainingSeconds: number } or null if not locked
 */
export function checkLockout() {
  const data = getLockoutData()
  if (!data || !data.lockedUntil) {
    return null
  }

  const now = Date.now()
  if (now < data.lockedUntil) {
    const remainingMs = data.lockedUntil - now
    const remainingSeconds = Math.ceil(remainingMs / 1000)
    return {
      isLocked: true,
      remainingSeconds,
      attempts: data.attempts
    }
  }

  // Lockout expired
  return null
}

/**
 * Record a failed login attempt
 * Returns lockout status if threshold reached
 */
export function recordFailedAttempt() {
  const data = getLockoutData() || { attempts: 0, lastAttempt: null, lockedUntil: null }

  // Increment attempt count
  data.attempts += 1
  data.lastAttempt = Date.now()

  // Check if we should apply lockout
  const lockoutMinutes = getLockoutDuration(data.attempts)

  if (lockoutMinutes > 0) {
    data.lockedUntil = Date.now() + (lockoutMinutes * 60 * 1000)
  }

  saveLockoutData(data)

  // Return lockout status
  if (data.lockedUntil) {
    const remainingMs = data.lockedUntil - Date.now()
    const remainingSeconds = Math.ceil(remainingMs / 1000)
    return {
      isLocked: true,
      remainingSeconds,
      attempts: data.attempts,
      lockoutMinutes
    }
  }

  return {
    isLocked: false,
    attempts: data.attempts,
    remainingAttempts: getNextThreshold(data.attempts)
  }
}

/**
 * Get remaining attempts until next lockout threshold
 */
function getNextThreshold(currentAttempts) {
  for (let threshold of LOCKOUT_THRESHOLDS) {
    if (currentAttempts < threshold.attempts) {
      return threshold.attempts - currentAttempts
    }
  }
  return 0
}

/**
 * Clear lockout data (call on successful login)
 */
export function clearLockout() {
  try {
    localStorage.removeItem(LOCKOUT_KEY)
  } catch (err) {
    console.error('Error clearing lockout:', err)
  }
}

/**
 * Format remaining time for display
 * @param {number} seconds - Remaining seconds
 * @returns {string} Formatted time (e.g., "2:30" or "45 seconds")
 */
export function formatLockoutTime(seconds) {
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (remainingSeconds === 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  }

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * Get lockout status message
 */
export function getLockoutMessage(lockoutStatus) {
  if (!lockoutStatus || !lockoutStatus.isLocked) {
    return null
  }

  const timeRemaining = formatLockoutTime(lockoutStatus.remainingSeconds)
  return `Account temporarily locked due to multiple failed login attempts. Please try again in ${timeRemaining}.`
}
